import { useState, useEffect, useRef } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonMenuButton,
  IonTitle,
  IonSearchbar,
  IonContent,
  IonList,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonRefresher,
  IonRefresherContent,
  useIonViewWillEnter,
  useIonViewWillLeave
} from '@ionic/react';

import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';

import Loader from './components/Loader';
import FileSingle from './components/FileSingle';
import TwoOFour from 'components/TwoOFour';

import barwisFileStore from 'stores/barwis-file.store';
import apolloClient from 'libs/apollo/apollo.lib';

import classes from './style.module.scss';

const BARWIS_FILE_FIELDS = `
  id
  name
  path
  created_at
  updated_at
`;

const FileExplorerPage = ({ match, history }) => {
  const [searchText, setSearchText] = useState(null);
  const [showListLoader, setShowListLoader] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [files, setFiles] = useState([]);
  const ionInfiniteScroll = useRef();
  const barwisFiles = barwisFileStore.getObjects();

  const handleFetch = async () => {
    try {
      await barwisFileStore.searchObjects();
      setShowListLoader(false);
    } catch (error) {
      console.log(error);
    }
  };

  useIonViewWillEnter(() => {
    barwisFileStore.setFields(BARWIS_FILE_FIELDS);
    barwisFileStore.setPage(1);
    barwisFileStore.setQuery({});
    const initData = async () => {
      setShowListLoader(true);
      await handleFetch();
    };
    initData();
  });

  useIonViewWillLeave(() => {
    barwisFileStore.resetAllFields();
    setFiles([]);
    setSearchText(null);
  });

  useEffect(() => {
    if (history.location.pathname === '/file-explorer') {
      setFiles((prevFiles) => [
        ...new Map(
          [...prevFiles, ...toJS(barwisFiles)].map((file) => [file['id'], file])
        ).values()
      ]);
    }
  }, [barwisFiles, history]);

  useEffect(() => {
    const searchFiles = () => {
      setFiles([]);
      setShowListLoader(true);
      if (searchText?.length > 2) {
        barwisFileStore.setPage(1);
        barwisFileStore.setQuery({
          column: 'name',
          operator: 'LIKE',
          value: `%${searchText}%`
        });
        barwisFileStore.abortAllPendingRequests();
        handleFetch();
      } else if (searchText !== null) {
        barwisFileStore.setQuery({});
        handleFetch();
      }
    };
    searchFiles();
  }, [searchText]);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot='start'>
            <IonMenuButton></IonMenuButton>
          </IonButtons>
          <IonTitle>File Explorer</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className={classes.ionContent}>
        <IonRefresher
          onIonRefresh={async (e) => {
            setIsRefreshing(true);
            barwisFileStore.abortAllPendingRequests();
            await apolloClient.clearStore();
            await handleFetch();
            e.detail.complete();
            setIsRefreshing(false);
          }}
          slot='fixed'
        >
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>
        <IonSearchbar
          value={searchText}
          onIonChange={(e) => setSearchText(e.detail.value)}
          color='transparent'
          inputMode='text'
          placeholder='Search File'
          mode='md'
          debounce={500}
          animated
        ></IonSearchbar>
        <IonList className={classes.ionList} lines='none'>
          {!showListLoader ? (
            files.length > 0 ? (
              files.map((barwisFile) => (
                <FileSingle barwisFile={barwisFile} key={barwisFile.id} />
              ))
            ) : (
              <TwoOFour message='You are not authorized to view any files!' />
            )
          ) : (
            Array.from({ length: 6 }).map((_, index) => <Loader key={index} />)
          )}
        </IonList>

        <IonInfiniteScroll
          ref={ionInfiniteScroll}
          threshold='50px'
          onIonInfinite={async (e) => {
            const currentPage = barwisFileStore.getPage();
            const lastPage = barwisFileStore.getPaginationInfo().lastPage;
            if (currentPage < lastPage) {
              const nextPage = currentPage + 1;
              barwisFileStore.setPage(nextPage);
              barwisFileStore.abortAllPendingRequests();
              await handleFetch();
            }
            await ionInfiniteScroll.current.complete();
          }}
        >
          <IonInfiniteScrollContent
            loadingSpinner='dots'
            loadingText='Loading more files...'
          ></IonInfiniteScrollContent>
        </IonInfiniteScroll>
      </IonContent>
    </IonPage>
  );
};

export default observer(FileExplorerPage);
